import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import CurrencyBitcoinIcon from '@mui/icons-material/CurrencyBitcoin';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useTranslation } from 'react-i18next';
import { stripePromise as defaultStripePromise } from '../../config/stripe';

const POLL_INTERVAL_MS = 5000;
const TERMINAL_CRYPTO_STATUSES = ['confirmed', 'failed', 'expired'];

const AmountSummary = ({ amount }) => {
  const { t } = useTranslation();

  return (
    <Box sx={{ mb: 3, p: 2, bgcolor: 'primary.light', borderRadius: 1 }}>
      <Typography variant="body2" color="primary.contrastText">
        {t('payment.amountToPay')}
      </Typography>
      <Typography variant="h4" color="primary.contrastText" fontWeight={700}>
        €{amount.toFixed(2)}
      </Typography>
    </Box>
  );
};

const createDefaultPaymentApi = () => ({
  createStripePaymentIntent: async ({ amount, currency, metadata }) => {
    const fn = httpsCallable(getFunctions(), 'createPaymentIntent');
    const result = await fn({ amount, currency, metadata });
    return result.data;
  },
  saveStripePaymentRecord: async ({ paymentIntentId, additionalData }) => {
    const fn = httpsCallable(getFunctions(), 'savePaymentRecord');
    const result = await fn({ paymentIntentId, additionalData });
    return result.data;
  },
  createNowPaymentsInvoice: async (payload) => {
    const fn = httpsCallable(getFunctions(), 'createNOWPaymentsInvoice');
    const result = await fn(payload);
    return result.data;
  },
  getNowPaymentsStatus: async ({ paymentId }) => {
    const fn = httpsCallable(getFunctions(), 'getNOWPaymentsStatus');
    const result = await fn({ paymentId });
    return result.data;
  },
});

const CheckoutForm = ({ amount, metadata, onSuccess, onCancel, onBack, paymentApi }) => {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements) return;

    setProcessing(true);
    setError(null);

    try {
      const { error: submitError, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/payment-success`,
        },
        redirect: 'if_required',
      });

      if (submitError) {
        setError(submitError.message);
        return;
      }

      if (paymentIntent?.status === 'succeeded') {
        try {
          await paymentApi.saveStripePaymentRecord({
            paymentIntentId: paymentIntent.id,
            additionalData: {
              ...metadata,
              amount,
              paymentProvider: 'stripe',
              completedAt: new Date().toISOString(),
            },
          });
        } catch (saveError) {
          console.error('Error saving Stripe payment record:', saveError);
        }

        onSuccess?.(paymentIntent);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <AmountSummary amount={amount} />

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('payment.enterCardDetails')}
      </Typography>

      <PaymentElement />

      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 3 }}>
        <Button
          onClick={onBack}
          variant="outlined"
          fullWidth
          disabled={processing}
          startIcon={<ArrowBackIcon />}
        >
          {t('common.back')}
        </Button>
        <Button onClick={onCancel} variant="outlined" fullWidth disabled={processing}>
          {t('common.cancel')}
        </Button>
        <Button
          type="submit"
          variant="contained"
          fullWidth
          disabled={!stripe || processing}
        >
          {processing
            ? <CircularProgress size={24} color="inherit" />
            : t('payment.payAmount', { amount: amount.toFixed(2) })}
        </Button>
      </Stack>
    </form>
  );
};

const StripePaymentContent = ({
  amount,
  metadata,
  onSuccess,
  onClose,
  onBack,
  paymentApi,
  stripePromise,
}) => {
  const { t } = useTranslation();
  const [clientSecret, setClientSecret] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const createIntent = async () => {
      try {
        const result = await paymentApi.createStripePaymentIntent({
          amount,
          currency: 'eur',
          metadata,
        });
        if (!cancelled) setClientSecret(result.clientSecret);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    if (amount > 0) createIntent();
    return () => { cancelled = true; };
  }, [amount, metadata, paymentApi]);

  if (loading) {
    return <Box display="flex" justifyContent="center" minHeight={200}><CircularProgress /></Box>;
  }

  if (error) {
    return <Alert severity="error">{t('payment.initializationFailed')}: {error}</Alert>;
  }

  return (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret,
        appearance: { theme: 'stripe', variables: { colorPrimary: '#1976d2' } },
      }}
    >
      <CheckoutForm
        amount={amount}
        metadata={metadata}
        onSuccess={onSuccess}
        onCancel={onClose}
        onBack={onBack}
        paymentApi={paymentApi}
      />
    </Elements>
  );
};

const CryptoPaymentContent = ({ amount, metadata, onSuccess, onClose, onBack, paymentApi }) => {
  const { t } = useTranslation();
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [opened, setOpened] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const createInvoice = async () => {
      try {
        const result = await paymentApi.createNowPaymentsInvoice({
          amount,
          currency: 'eur',
          metadata,
          successUrl: window.location.href,
          cancelUrl: window.location.href,
        });
        if (!cancelled) setPayment(result);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    if (amount > 0) createInvoice();
    return () => { cancelled = true; };
  }, [amount, metadata, paymentApi]);

  useEffect(() => {
    if (!opened || !payment?.paymentId) return undefined;
    if (TERMINAL_CRYPTO_STATUSES.includes(payment.status)) return undefined;

    let cancelled = false;
    const interval = window.setInterval(async () => {
      try {
        const result = await paymentApi.getNowPaymentsStatus({ paymentId: payment.paymentId });
        if (cancelled) return;

        const status = result.status;
        setPayment((current) => ({ ...current, status }));
        if (status === 'confirmed') {
          onSuccess?.({ ...payment, status, provider: 'nowpayments' });
        }
      } catch (pollError) {
        console.error('Error checking NOWPayments status:', pollError);
      }
    }, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [opened, onSuccess, payment, paymentApi]);

  if (loading) {
    return <Box display="flex" justifyContent="center" minHeight={200}><CircularProgress /></Box>;
  }

  if (error) {
    return (
      <>
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('payment.initializationFailed')}: {error}
        </Alert>
        <Button onClick={onBack} startIcon={<ArrowBackIcon />}>
          {t('common.back')}
        </Button>
      </>
    );
  }

  const failed = ['failed', 'expired'].includes(payment?.status);

  return (
    <Box>
      <AmountSummary amount={amount} />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('payment.cryptoDescription')}
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Button
          variant="contained"
          fullWidth
          endIcon={<OpenInNewIcon />}
          onClick={() => {
            window.open(payment.hostedUrl, '_blank', 'noopener,noreferrer');
            setOpened(true);
          }}
        >
          {opened ? t('payment.reopenCryptoPayment') : t('payment.continueToCryptoPayment')}
        </Button>
      </Paper>

      {opened && !failed && payment.status !== 'confirmed' && (
        <Alert severity="info" icon={<CircularProgress size={18} />} sx={{ mb: 2 }}>
          {payment.status === 'confirming'
            ? t('payment.cryptoConfirming')
            : t('payment.cryptoWaiting')}
        </Alert>
      )}
      {payment.status === 'confirmed' && (
        <Alert severity="success" sx={{ mb: 2 }}>{t('payment.cryptoConfirmed')}</Alert>
      )}
      {failed && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {payment.status === 'expired'
            ? t('payment.cryptoExpired')
            : t('payment.cryptoFailed')}
        </Alert>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
        <Button onClick={onBack} variant="outlined" fullWidth startIcon={<ArrowBackIcon />}>
          {t('common.back')}
        </Button>
        <Button onClick={onClose} variant="outlined" fullWidth>
          {t('common.cancel')}
        </Button>
      </Stack>
    </Box>
  );
};

const PaymentMethodSelector = ({ amount, onSelect }) => {
  const { t } = useTranslation();
  const methods = [
    {
      id: 'stripe',
      icon: <CreditCardIcon />,
      title: t('payment.cardTitle'),
      description: t('payment.cardDescription'),
    },
    {
      id: 'nowpayments',
      icon: <CurrencyBitcoinIcon />,
      title: t('payment.cryptoTitle'),
      description: t('payment.cryptoMethodDescription'),
    },
  ];

  return (
    <Box>
      <AmountSummary amount={amount} />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('payment.choosePaymentMethod')}
      </Typography>
      <Stack spacing={1.5}>
        {methods.map((method) => (
          <Paper
            key={method.id}
            variant="outlined"
            onClick={() => onSelect(method.id)}
            sx={{
              p: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              cursor: 'pointer',
              '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
            }}
          >
            <Box sx={{ color: 'primary.main', display: 'flex' }}>{method.icon}</Box>
            <Box>
              <Typography variant="subtitle2" fontWeight={700}>{method.title}</Typography>
              <Typography variant="caption" color="text.secondary">
                {method.description}
              </Typography>
            </Box>
          </Paper>
        ))}
      </Stack>
    </Box>
  );
};

export const PaymentModal = ({
  open,
  amount,
  onSuccess,
  onClose,
  title,
  metadata = {},
  paymentApi,
  stripePromise = defaultStripePromise,
}) => {
  const { t } = useTranslation();
  const [method, setMethod] = useState(null);
  const resolvedPaymentApi = useMemo(
    () => paymentApi || createDefaultPaymentApi(),
    [paymentApi]
  );

  useEffect(() => {
    if (!open) setMethod(null);
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" fontWeight={600}>
            {title || t('payment.completePayment')}
          </Typography>
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 3 }}>
        {!method && <PaymentMethodSelector amount={amount} onSelect={setMethod} />}
        {method === 'stripe' && (
          <StripePaymentContent
            amount={amount}
            metadata={metadata}
            onSuccess={onSuccess}
            onClose={onClose}
            onBack={() => setMethod(null)}
            paymentApi={resolvedPaymentApi}
            stripePromise={stripePromise}
          />
        )}
        {method === 'nowpayments' && (
          <CryptoPaymentContent
            amount={amount}
            metadata={metadata}
            onSuccess={onSuccess}
            onClose={onClose}
            onBack={() => setMethod(null)}
            paymentApi={resolvedPaymentApi}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PaymentModal;
